import { useCallback, useState } from 'react'
import { ApiClientError } from '@/lib/api/client'
import {
  landingCheckDonorPhoneAccess,
  landingDonorForgotPasswordByPhone,
  landingDonorLoginByPhone,
  landingDonorRegister,
  landingFetchDonorMe,
  type LandingDonorAccessStatus,
} from '@/lib/landing-auth'
import { writeLandingDonorToken } from '@/lib/landing-donor-session'
import {
  registerScratchParticipantAsDonor,
  saveScratchToken,
} from '@/lib/api/scratch-campaigns-public.service'
import {
  buildScratchRegisterPayload,
  maskBrazilianPhone,
  normalizeFullName,
  onlyPhoneDigits,
  type ScratchRegisterFormValues,
  validateBrazilianMobilePhone,
  validateScratchRegisterForm,
} from '@/lib/person-data'

export type RaspadinhaRegisterFlowStep =
  | 'phone'
  | 'password'
  | 'recover-password'
  | 'recovery-sent'
  | 'register'

export type RaspadinhaRegisterFlowContent = {
  title: string
  description: string
  buttonLabel: string
  loadingLabel: string
  notice: string | null
}

const EMPTY_REGISTER_FORM: ScratchRegisterFormValues = {
  name: '',
  email: '',
  phone: '',
  document: '',
}

function resolveSubmitError(error: unknown, fallback: string): string {
  if (error instanceof ApiClientError) {
    const fieldMessages = error.errors
      ? Object.values(error.errors)
          .flat()
          .filter((message) => message.trim().length > 0)
      : []
    if (fieldMessages.length > 0) {
      return fieldMessages[0]
    }
    return error.message
  }

  return fallback
}

export function getRaspadinhaRegisterFlowContent(
  step: RaspadinhaRegisterFlowStep
): RaspadinhaRegisterFlowContent {
  switch (step) {
    case 'password':
      return {
        title: 'Conta encontrada',
        description: 'Digite sua senha da Sortee para continuar na raspadinha.',
        buttonLabel: 'Entrar e raspar',
        loadingLabel: 'Entrando...',
        notice: null,
      }
    case 'recover-password':
      return {
        title: 'Recuperar acesso à Sortee',
        description:
          'Receba um link no WhatsApp cadastrado para definir sua senha com segurança.',
        buttonLabel: 'Enviar link de recuperação',
        loadingLabel: 'Enviando...',
        notice: null,
      }
    case 'recovery-sent':
      return {
        title: 'Verifique seu WhatsApp',
        description:
          'Se houver uma conta, você receberá o link. Depois de definir a senha, volte aqui para continuar.',
        buttonLabel: 'Voltar ao login',
        loadingLabel: 'Voltando...',
        notice: null,
      }
    case 'register':
      return {
        title: 'Complete seu cadastro',
        description: 'Preencha seus dados para criar sua conta Sortee e participar.',
        buttonLabel: 'Cadastrar e raspar',
        loadingLabel: 'Cadastrando...',
        notice: 'Você poderá criar uma senha depois, se desejar.',
      }
    case 'phone':
    default:
      return {
        title: 'Informe seu WhatsApp',
        description:
          'Usamos seu número para localizar seu cadastro e agilizar a participação.',
        buttonLabel: 'Continuar',
        loadingLabel: 'Verificando...',
        notice: null,
      }
  }
}

function mapAccessStatusToStep(
  status: LandingDonorAccessStatus
): RaspadinhaRegisterFlowStep {
  switch (status) {
    case 'password_required':
      return 'password'
    case 'password_creation_required':
      return 'recover-password'
    case 'registration_required':
    default:
      return 'register'
  }
}

type UseRaspadinhaRegisterFlowParams = {
  slug: string
  institutionId: number | null
  onRegistered: () => void
}

export function useRaspadinhaRegisterFlow({
  slug,
  institutionId,
  onRegistered,
}: UseRaspadinhaRegisterFlowParams) {
  const [flowStep, setFlowStep] = useState<RaspadinhaRegisterFlowStep>('phone')
  const [phone, setPhone] = useState('')
  const [loginPassword, setLoginPassword] = useState('')

  const [registerForm, setRegisterForm] =
    useState<ScratchRegisterFormValues>(EMPTY_REGISTER_FORM)
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof ScratchRegisterFormValues, string>>
  >({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const completeScratchRegistration = useCallback(async () => {
    await registerScratchParticipantAsDonor(slug)
    saveScratchToken(slug)
    onRegistered()
  }, [onRegistered, slug])

  const handleCheckPhone = useCallback(async () => {
    const phoneError = validateBrazilianMobilePhone(phone)
    if (phoneError) {
      setSubmitError(phoneError.message)
      return
    }

    setSubmitError(null)
    setIsSubmitting(true)

    try {
      const normalizedPhone = onlyPhoneDigits(phone)
      const signedInDonor = await landingFetchDonorMe('cookie').catch(
        (error: unknown) => {
          if (error instanceof ApiClientError && error.status === 401) return null
          throw error
        }
      )
      if (
        signedInDonor &&
        (signedInDonor.phone ?? '').replace(/\D/g, '').replace(/^55(?=\d{11}$)/, '') ===
          normalizedPhone
      ) {
        writeLandingDonorToken('cookie')
        await completeScratchRegistration()
        return
      }
      const response = await landingCheckDonorPhoneAccess({
        institution_id: institutionId ?? undefined,
        phone: normalizedPhone,
      })

      setPhone(maskBrazilianPhone(response.phone || normalizedPhone))
      setRegisterForm((previous) => ({
        ...previous,
        phone: maskBrazilianPhone(response.phone || normalizedPhone),
      }))
      setFlowStep(
        response.has_account && !response.has_password
          ? 'recover-password'
          : mapAccessStatusToStep(response.status)
      )
      setLoginPassword('')

      setFieldErrors({})
    } catch (error) {
      setSubmitError(
        resolveSubmitError(error, 'Não foi possível verificar seu telefone agora.')
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [completeScratchRegistration, institutionId, phone])

  const handleLogin = useCallback(async () => {
    setSubmitError(null)
    setIsSubmitting(true)

    try {
      await landingDonorLoginByPhone({
        institution_id: institutionId ?? undefined,
        phone: onlyPhoneDigits(phone),
        password: loginPassword,
      })
      await completeScratchRegistration()
    } catch (error) {
      setSubmitError(
        resolveSubmitError(error, 'Não foi possível entrar. Verifique seus dados.')
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [completeScratchRegistration, institutionId, loginPassword, phone])

  const handleRecoverAccess = useCallback(async () => {
    setSubmitError(null)
    setIsSubmitting(true)
    try {
      await landingDonorForgotPasswordByPhone({ phone: onlyPhoneDigits(phone) })
      setFlowStep('recovery-sent')
    } catch (error) {
      setSubmitError(
        resolveSubmitError(error, 'Não foi possível enviar o link de recuperação.')
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [phone])

  const handleRegisterNewDonor = useCallback(async () => {
    const formWithPhone: ScratchRegisterFormValues = {
      ...registerForm,
      phone: phone || registerForm.phone,
    }

    const errors = validateScratchRegisterForm(formWithPhone)
    if (errors.length > 0) {
      setFieldErrors(
        errors.reduce<Partial<Record<keyof ScratchRegisterFormValues, string>>>(
          (accumulator, error) => {
            accumulator[error.field] = error.message
            return accumulator
          },
          {}
        )
      )
      return
    }

    setFieldErrors({})
    setSubmitError(null)
    setIsSubmitting(true)

    const payload = buildScratchRegisterPayload(formWithPhone)

    try {
      await landingDonorRegister({
        institution_id: institutionId ?? undefined,
        person_type: 'pf',
        full_name: normalizeFullName(payload.name),
        document_cpf: payload.document,
        email: payload.email,
        phone: payload.phone,
      })
      await completeScratchRegistration()
    } catch (error) {
      setSubmitError(
        resolveSubmitError(
          error,
          'Não foi possível concluir o cadastro. Tente novamente.'
        )
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [completeScratchRegistration, institutionId, phone, registerForm])

  const handlePrimaryAction = useCallback(async () => {
    switch (flowStep) {
      case 'phone':
        await handleCheckPhone()
        break
      case 'password':
        await handleLogin()
        break
      case 'recover-password':
        await handleRecoverAccess()
        break
      case 'recovery-sent':
        setFlowStep('password')
        break
      case 'register':
        await handleRegisterNewDonor()
        break
      default:
        break
    }
  }, [
    flowStep,
    handleCheckPhone,
    handleRecoverAccess,
    handleLogin,
    handleRegisterNewDonor,
  ])

  const updateRegisterField = useCallback(
    (field: keyof ScratchRegisterFormValues, value: string) => {
      setRegisterForm((previous) => ({ ...previous, [field]: value }))
      setFieldErrors((previous) => {
        if (!previous[field]) return previous
        const next = { ...previous }
        delete next[field]
        return next
      })
    },
    []
  )

  const editPhone = useCallback(() => {
    setFlowStep('phone')
    setSubmitError(null)
    setLoginPassword('')

    setFieldErrors({})
  }, [])

  return {
    flowStep,
    flowContent: getRaspadinhaRegisterFlowContent(flowStep),
    phone,
    setPhone,
    loginPassword,
    setLoginPassword,

    registerForm,
    updateRegisterField,
    fieldErrors,
    submitError,
    isSubmitting,
    handlePrimaryAction,
    editPhone,
    startRecovery: () => setFlowStep('recover-password'),
  }
}
